import { useState } from "react";
import { 
    BuildingStorefrontIcon,
    HomeModernIcon,
    CakeIcon,
    MapPinIcon,
    CheckCircleIcon
} from "@heroicons/react/24/outline";
import { setOnboardingStorage } from "utils/onboardingStorage";

const OnboardingChrTargeting = (props: { onNext: any; onPrev: any }) => {
    const { onNext, onPrev } = props;

    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

    const chrTypes = [
        {
            id: "cafe",
            label: "Cafés",
            icon: BuildingStorefrontIcon,
            description: "Coffee shops, salons de thé, bars"
        },
        {
            id: "hotel",
            label: "Hôtels",
            icon: HomeModernIcon,
            description: "Hôtels, résidences, chambres d'hôtes"
        },
        {
            id: "restaurant",
            label: "Restaurants",
            icon: CakeIcon,
            description: "Restaurants traditionnels, gastronomiques"
        },
        {
            id: "fast-food",
            label: "Restauration rapide",
            icon: MapPinIcon,
            description: "Fast-foods, snacks, food trucks"
        }
    ];

    const targetCategories = [
        { id: "independant", label: "Indépendants" },
        { id: "chain-local", label: "Chaînes locales (2-10 établissements)" },
        { id: "chain-national", label: "Chaînes nationales" },
        { id: "franchise", label: "Franchises" },
        { id: "luxury", label: "Haut de gamme / Luxe" },
        { id: "all", label: "Tous types confondus" }
    ];

    const toggleType = (id: string) => {
        if (selectedTypes.includes(id)) {
            setSelectedTypes(selectedTypes.filter(item => item !== id));
        } else {
            setSelectedTypes([...selectedTypes, id]);
        }
    };

    const toggleCategory = (id: string) => {
        if (id === "all") {
            setSelectedCategories(["all"]);
        } else {
            let newSelection = selectedCategories.filter(item => item !== "all");
            if (selectedCategories.includes(id)) {
                newSelection = newSelection.filter(item => item !== id);
            } else {
                newSelection = [...newSelection, id];
            }
            setSelectedCategories(newSelection);
        }
    };

    const handleContinue = () => {
        if (selectedTypes.length === 0) {
            alert("Veuillez sélectionner au moins un type d'établissement");
            return;
        }
        if (selectedCategories.length === 0) {
            alert("Veuillez sélectionner au moins une catégorie");
            return;
        }
        setOnboardingStorage("chrTypes", selectedTypes.join(","));
        setOnboardingStorage("chrCategories", selectedCategories.join(","));
        onNext();
    };

    return (
        <div className="flex flex-col items-center justify-between min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-6 py-12">
            <div className="w-full max-w-5xl">
                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 text-center">
                    Quels établissements CHR ciblez-vous ?
                </h1>
                <p className="text-gray-500 mb-10 text-center max-w-2xl mx-auto">
                    Aidez-nous à mieux comprendre votre cible pour personnaliser votre expérience
                </p>

                {/* CHR Types */}
                <div className="w-full mb-10">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">
                        Types d'établissements
                    </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {chrTypes.map((type) => {
                        const Icon = type.icon;
                        const isSelected = selectedTypes.includes(type.id);
                        
                        return (
                            <button
                                key={type.id}
                                onClick={() => toggleType(type.id)}
                                className={`p-5 rounded-2xl border transition-all text-left hover:shadow-md ${
                                    isSelected
                                        ? "border-orange-400 bg-orange-50/50 shadow-sm"
                                        : "border-gray-200 bg-white hover:border-gray-300"
                                }`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-xl ${
                                        isSelected ? "bg-gradient-to-br from-orange-400 to-orange-500" : "bg-gray-100"
                                    }`}>
                                        <Icon className={`h-6 w-6 ${
                                            isSelected ? "text-white" : "text-gray-500"
                                        }`} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className={`font-semibold mb-1 ${
                                            isSelected ? "text-orange-700" : "text-gray-900"
                                        }`}>
                                            {type.label}
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {type.description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

                {/* Target Categories */}
                <div className="w-full mb-8">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">
                        Catégories ciblées
                    </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {targetCategories.map((category) => {
                        const isSelected = selectedCategories.includes(category.id);
                        
                        return (
                            <button
                                key={category.id}
                                onClick={() => toggleCategory(category.id)}
                                className={`p-4 rounded-xl border transition-all text-center hover:shadow-sm relative ${
                                    isSelected
                                        ? "border-orange-400 bg-orange-50/50"
                                        : "border-gray-200 bg-white hover:border-gray-300"
                                }`}
                            >
                                {isSelected && (
                                    <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5 text-orange-500" />
                                )}
                                <p className={`font-medium text-sm ${
                                    isSelected ? "text-orange-700" : "text-gray-900"
                                }`}>
                                    {category.label}
                                </p>
                            </button>
                        );
                    })}
                </div>
            </div>
            </div>

            {/* Bottom Navigation */}
            <div className="w-full max-w-2xl">
                <div className="flex gap-4">
                    <button
                        className="px-6 py-4 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all"
                        onClick={onPrev}
                    >
                        Précédent
                    </button>
                    <button
                        className={`flex-1 px-8 py-4 font-semibold rounded-xl transition-all text-lg ${
                            selectedTypes.length > 0 && selectedCategories.length > 0
                                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-orange-700"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                        onClick={handleContinue}
                        disabled={!(selectedTypes.length > 0 && selectedCategories.length > 0)}
                    >
                        Continuer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OnboardingChrTargeting;
